import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Activity,
  Search,
  X,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Building2,
  Landmark,
  Hammer,
  FileText,
  FolderKanban,
  ShieldCheck,
  CircleUserRound,
  SlidersHorizontal,
  Clock3,
  ArrowRight,
  Pencil,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Power,
  PowerOff,
  Send,
  UserPlus,
  UserMinus,
  ReceiptText,
  Info,
  Database,
  WalletCards,
} from "lucide-react";

import "./AdminPlatformActivity.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const AREA_FILTERS = [
  {
    value: "ALL",
    icon: Activity,
  },
  {
    value: "ACCOUNT",
    icon: CircleUserRound,
  },
  {
    value: "ORGANISATION",
    icon: Building2,
  },
  {
    value: "DOSSIER",
    icon: FolderKanban,
  },
  {
    value: "LOAN",
    icon: Landmark,
  },
  {
    value: "RENOVATION",
    icon: Hammer,
  },
  {
    value: "INVOICE",
    icon: ReceiptText,
  },
  {
    value: "DOCUMENT",
    icon: FileText,
  },
  {
    value: "OSS",
    icon: Building2,
  },
  {
    value: "ADMIN",
    icon: ShieldCheck,
  },
];

const ACTION_OPTIONS = [
  "ALL",
  "CREATED",
  "UPDATED",
  "DELETED",
  "STATUS_CHANGED",
  "SUBMITTED",
  "APPROVED",
  "REJECTED",
  "ACTIVATED",
  "DEACTIVATED",
  "ASSIGNED",
  "UNASSIGNED",
  "REQUESTED",
];

const ROLE_OPTIONS = [
  "ALL",
  "HOMEOWNER",
  "LENDER",
  "OSS",
  "INVESTOR",
  "ADMIN",
  "PLATFORM_ADMIN",
  "SYSTEM",
];

const DATE_PRESETS = ["ALL", "7_DAYS", "30_DAYS", "90_DAYS", "CUSTOM"];

const PAGE_SIZE = 20;

/* =========================================================
   CURRENT BACKEND CONTEXT KEYS
   ========================================================= */

const CURRENT_CONTEXT_KEYS = [
  "current_loan_result",
  "current_loan_case",
  "current_funding_sources",
  "current_funding_source_count",
  "current_project_funding_source",
  "current_funding_type",
];

/* =========================================================
   DATE HELPERS
   ========================================================= */

function toLocalDateString(date) {
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-` + `${month}-` + `${day}`;
}

function getPresetDates(preset) {
  if (preset === "ALL" || preset === "CUSTOM") {
    return {
      dateFrom: "",
      dateTo: "",
    };
  }

  const today = new Date();

  const from = new Date(today);

  const daysMap = {
    "7_DAYS": 7,
    "30_DAYS": 30,
    "90_DAYS": 90,
  };

  const days = daysMap[preset];

  if (!days) {
    return {
      dateFrom: "",
      dateTo: "",
    };
  }

  from.setDate(today.getDate() - (days - 1));

  return {
    dateFrom: toLocalDateString(from),

    dateTo: toLocalDateString(today),
  };
}

/* =========================================================
   DISPLAY HELPERS
   ========================================================= */

function getInitials(name = "") {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "S"
  );
}

function getActionIcon(action) {
  switch (action) {
    case "CREATED":
      return Plus;

    case "UPDATED":
    case "STATUS_CHANGED":
      return Pencil;

    case "DELETED":
      return Trash2;

    case "APPROVED":
      return CheckCircle2;

    case "REJECTED":
      return XCircle;

    case "ACTIVATED":
      return Power;

    case "DEACTIVATED":
      return PowerOff;

    case "SUBMITTED":
      return Send;

    case "ASSIGNED":
      return UserPlus;

    case "UNASSIGNED":
      return UserMinus;

    case "REQUESTED":
      return Send;

    default:
      return Activity;
  }
}

function getAreaIcon(area) {
  switch (area) {
    case "ACCOUNT":
      return CircleUserRound;

    case "ORGANISATION":
      return Building2;

    case "DOSSIER":
      return FolderKanban;

    case "LOAN":
      return Landmark;

    case "RENOVATION":
      return Hammer;

    case "INVOICE":
      return ReceiptText;

    case "DOCUMENT":
      return FileText;

    case "OSS":
      return Building2;

    case "ADMIN":
      return ShieldCheck;

    default:
      return Activity;
  }
}

function humanizeKey(value = "") {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatContextKey(key) {
  return humanizeKey(String(key || "").replace(/^current_/, ""));
}

function getStatusClass(status) {
  const value = String(status || "").toUpperCase();

  if (
    ["APPROVED", "CONFIRMED", "ACTIVE", "COMPLETED", "ACCEPTED"].includes(value)
  ) {
    return "is-success";
  }

  if (["EXPECTED", "ESTIMATED", "PENDING", "DRAFT"].includes(value)) {
    return "is-pending";
  }

  if (["REJECTED", "DECLINED", "CANCELLED", "DEACTIVATED"].includes(value)) {
    return "is-danger";
  }

  return "";
}

function formatAmount(value, currency = "EUR") {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return (`${value} ` + `${currency || ""}`).trim();
  }

  try {
    return new Intl.NumberFormat("en", {
      style: "currency",

      currency: currency || "EUR",

      maximumFractionDigits: 2,
    }).format(number);
  } catch {
    return (`${number.toLocaleString()} ` + `${currency || ""}`).trim();
  }
}

/* =========================================================
   EVENT NORMALISER
   ========================================================= */

function normaliseAuditEvent(event = {}) {
  /* =======================================================
     ACTUAL OPERATOR
     ======================================================= */

  const actorName = event.actor_name || event.actor?.name || "System";

  const actorRole =
    event.actor_role ||
    event.actor?.role ||
    (actorName === "System" ? "SYSTEM" : "");

  const organisationName =
    event.organisation_name || event.actor?.organisation?.name || "";

  const actorEmail = event.actor_email || event.actor?.email || "";

  /* =======================================================
     ACTING / ON-BEHALF-OF CONTEXT
     ======================================================= */

  const actingRole = event.acting_role || event.metadata?.acting_role || "";

  const actingOrganisationId =
    event.acting_organisation ?? event.metadata?.acting_organisation_id ?? null;

  const actingOrganisationName =
    event.acting_organisation_name ||
    event.metadata?.acting_organisation_name ||
    "";

  /* =======================================================
     TARGET
     ======================================================= */

  const targetType = event.target_type || event.target?.type || "";

  const targetId = event.target_id ?? event.target?.id ?? null;

  const targetLabel =
    event.target_label || event.target?.label || targetType || "";

  return {
    ...event,

    actorName,
    actorRole,
    actorEmail,
    organisationName,

    actingRole,
    actingOrganisationId,
    actingOrganisationName,

    targetType,
    targetId,
    targetLabel,
  };
}

/* =========================================================
   GENERIC VALUE HELPERS
   ========================================================= */

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function getDisplayValue(value) {
  if (value === null || value === undefined || value === "") {
    return {
      type: "empty",
      value: "—",
    };
  }

  if (typeof value === "boolean") {
    return {
      type: "text",
      value: value ? "Yes" : "No",
    };
  }

  if (Array.isArray(value)) {
    return {
      type: "array",
      value,
    };
  }

  if (isPlainObject(value)) {
    return {
      type: "object",
      value,
    };
  }

  return {
    type: "text",
    value: String(value),
  };
}

/* =========================================================
   GENERIC AUDIT VALUE
   ========================================================= */

function AuditValue({ value, emptyLabel = "—" }) {
  const display = getDisplayValue(value);

  /* -------------------------------------------------------
     EMPTY
     ------------------------------------------------------- */

  if (display.type === "empty") {
    return (
      <span className="admin-activity-value admin-activity-value--empty">
        {emptyLabel}
      </span>
    );
  }

  /* -------------------------------------------------------
     ARRAY
     ------------------------------------------------------- */

  if (display.type === "array") {
    if (display.value.length === 0) {
      return (
        <span className="admin-activity-value admin-activity-value--empty">
          {emptyLabel}
        </span>
      );
    }

    const containsObjects = display.value.some((item) => isPlainObject(item));

    if (containsObjects) {
      return (
        <div className="admin-activity-value-object-list">
          {display.value.map((item, index) => (
            <div
              key={item?.id || item?.name || item?.code || index}
              className="admin-activity-value-object-list__item"
            >
              <AuditValue value={item} />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="admin-activity-value-list">
        {display.value.map((item, index) => (
          <span
            key={`${String(item)}-${index}`}
            className="admin-activity-value-chip"
          >
            {String(item)}
          </span>
        ))}
      </div>
    );
  }

  /* -------------------------------------------------------
     OBJECT
     ------------------------------------------------------- */

  if (display.type === "object") {
    return (
      <div className="admin-activity-object-value">
        {Object.entries(display.value).map(([key, nestedValue]) => (
          <div key={key} className="admin-activity-object-value__row">
            <span className="admin-activity-object-value__key">
              {humanizeKey(key)}
            </span>

            <div className="admin-activity-object-value__content">
              <AuditValue value={nestedValue} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  /* -------------------------------------------------------
     TEXT
     ------------------------------------------------------- */

  return <span className="admin-activity-value">{display.value}</span>;
}

/* =========================================================
   FUNDING SOURCE VALUE
   ========================================================= */

function FundingSourcesValue({ value }) {
  if (!Array.isArray(value) || value.length === 0) {
    return (
      <span className="admin-activity-value admin-activity-value--empty">
        —
      </span>
    );
  }

  return (
    <div className="admin-activity-funding-list">
      {value.map((source, index) => {
        /*
         * The backend may still return old/string
         * values for historical events.
         */
        if (!isPlainObject(source)) {
          return (
            <div
              key={`${String(source)}-${index}`}
              className="admin-activity-funding-source"
            >
              <div className="admin-activity-funding-source__icon">
                <WalletCards size={16} strokeWidth={2} />
              </div>

              <div className="admin-activity-funding-source__body">
                <strong>{String(source)}</strong>
              </div>
            </div>
          );
        }

        const type =
          source.type || source.source_type || source.source_type_code || "";

        const typeLabel =
          source.type_label ||
          source.source_type_label ||
          source.source_type_name ||
          humanizeKey(type) ||
          "Funding source";

        const name = source.name || source.financing_product || "";

        const amount =
          source.amount ??
          source.approved_amount ??
          source.requested_amount ??
          null;

        const currency = source.currency || "EUR";

        const sourceStatus = source.status || "";

        return (
          <div
            key={source.id || `${type}-${name}-${index}`}
            className="admin-activity-funding-source"
          >
            <div className="admin-activity-funding-source__icon">
              <WalletCards size={16} strokeWidth={2} />
            </div>

            <div className="admin-activity-funding-source__body">
              <div className="admin-activity-funding-source__title-row">
                <div>
                  <span className="admin-activity-funding-source__type">
                    {typeLabel}
                  </span>

                  {name ? <strong>{name}</strong> : null}
                </div>

                {sourceStatus ? (
                  <span
                    className={`admin-activity-context-status ${getStatusClass(
                      sourceStatus,
                    )}`}
                  >
                    {humanizeKey(sourceStatus)}
                  </span>
                ) : null}
              </div>

              {amount !== null ? (
                <span className="admin-activity-funding-source__amount">
                  {formatAmount(amount, currency)}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* =========================================================
   CURRENT CONTEXT VALUE
   ========================================================= */

function CurrentContextValue({ contextKey, value }) {
  if (contextKey === "current_funding_sources") {
    return <FundingSourcesValue value={value} />;
  }

  if (
    contextKey === "current_funding_source_count" &&
    value !== null &&
    value !== undefined
  ) {
    return <span className="admin-activity-context-count">{value}</span>;
  }

  return <AuditValue value={value} />;
}

/* =========================================================
   PAGE
   ========================================================= */

export default function AdminPlatformActivity() {
  const { t, i18n } = useTranslation("admin-activity");

  const { getAccessTokenSilently } = useAuth0();

  /* =======================================================
     FILTER STATE
     ======================================================= */

  const [area, setArea] = React.useState("ALL");

  const [action, setAction] = React.useState("ALL");

  const [actorRole, setActorRole] = React.useState("ALL");

  const [searchInput, setSearchInput] = React.useState("");

  const [search, setSearch] = React.useState("");

  const [datePreset, setDatePreset] = React.useState("ALL");

  const [dateFrom, setDateFrom] = React.useState("");

  const [dateTo, setDateTo] = React.useState("");

  const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false);

  /* =======================================================
     DATA STATE
     ======================================================= */

  const [events, setEvents] = React.useState([]);

  const [pagination, setPagination] = React.useState({
    page: 1,
    page_size: PAGE_SIZE,
    total_items: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  });

  const [page, setPage] = React.useState(1);

  const [loading, setLoading] = React.useState(true);

  const [refreshing, setRefreshing] = React.useState(false);

  const [error, setError] = React.useState("");

  /* =======================================================
     EXPANDED EVENT
     ======================================================= */

  const [expandedEventId, setExpandedEventId] = React.useState(null);

  /* =======================================================
     SEARCH DEBOUNCE
     ======================================================= */

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());

      setPage(1);
    }, 350);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [searchInput]);

  /* =======================================================
     API QUERY
     ======================================================= */

  const buildQuery = React.useCallback(() => {
    const params = new URLSearchParams();

    params.set("page", String(page));

    params.set("page_size", String(PAGE_SIZE));

    if (area !== "ALL") {
      params.set("area", area);
    }

    if (action !== "ALL") {
      params.set("action", action);
    }

    if (actorRole !== "ALL") {
      params.set("actor_role", actorRole);
    }

    if (search) {
      params.set("search", search);
    }

    if (dateFrom) {
      params.set("date_from", dateFrom);
    }

    if (dateTo) {
      params.set("date_to", dateTo);
    }

    return params.toString();
  }, [page, area, action, actorRole, search, dateFrom, dateTo]);

  const loadActivity = React.useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}/api/admin/activity/?${buildQuery()}`,
          {
            method: "GET",

            headers: {
              Accept: "application/json",

              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data?.detail ||
              t("errors.load", "Could not load platform activity."),
          );
        }

        setEvents(Array.isArray(data?.results) ? data.results : []);

        setPagination({
          page: data?.pagination?.page || 1,

          page_size: data?.pagination?.page_size || PAGE_SIZE,

          total_items: data?.pagination?.total_items || 0,

          total_pages: data?.pagination?.total_pages || 0,

          has_next: Boolean(data?.pagination?.has_next),

          has_previous: Boolean(data?.pagination?.has_previous),
        });

        setExpandedEventId(null);
      } catch (err) {
        console.error("Could not load admin activity:", err);

        setEvents([]);

        setError(
          err?.message || t("errors.load", "Could not load platform activity."),
        );
      } finally {
        setLoading(false);

        setRefreshing(false);
      }
    },
    [buildQuery, getAccessTokenSilently, t],
  );

  React.useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  /* =======================================================
     FILTER HANDLERS
     ======================================================= */

  const handleAreaChange = (value) => {
    setArea(value);

    setPage(1);

    setExpandedEventId(null);
  };

  const handleActionChange = (event) => {
    setAction(event.target.value);

    setPage(1);

    setExpandedEventId(null);
  };

  const handleRoleChange = (event) => {
    setActorRole(event.target.value);

    setPage(1);

    setExpandedEventId(null);
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset);

    setPage(1);

    setExpandedEventId(null);

    if (preset === "CUSTOM") {
      return;
    }

    const {
      dateFrom: nextFrom,

      dateTo: nextTo,
    } = getPresetDates(preset);

    setDateFrom(nextFrom);

    setDateTo(nextTo);
  };

  const handleDateFromChange = (event) => {
    setDatePreset("CUSTOM");

    setDateFrom(event.target.value);

    setPage(1);

    setExpandedEventId(null);
  };

  const handleDateToChange = (event) => {
    setDatePreset("CUSTOM");

    setDateTo(event.target.value);

    setPage(1);

    setExpandedEventId(null);
  };

  const clearFilters = () => {
    setArea("ALL");

    setAction("ALL");

    setActorRole("ALL");

    setSearchInput("");

    setSearch("");

    setDatePreset("ALL");

    setDateFrom("");

    setDateTo("");

    setPage(1);

    setExpandedEventId(null);
  };

  const hasActiveFilters =
    area !== "ALL" ||
    action !== "ALL" ||
    actorRole !== "ALL" ||
    Boolean(search) ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  /* =======================================================
     FORMATTERS
     ======================================================= */

  const dateTimeFormatter = React.useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language || "en", {
        day: "2-digit",

        month: "short",

        year: "numeric",

        hour: "2-digit",

        minute: "2-digit",
      }),
    [i18n.language],
  );

  const formatDateTime = React.useCallback(
    (value) => {
      if (!value) {
        return "—";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return dateTimeFormatter.format(date);
    },
    [dateTimeFormatter],
  );

  /* =======================================================
     PAGE NUMBERS
     ======================================================= */

  const visiblePages = React.useMemo(() => {
    const totalPages = pagination.total_pages;

    if (!totalPages) {
      return [];
    }

    const candidates = new Set([1, totalPages, page - 1, page, page + 1]);

    return [...candidates]
      .filter((value) => value >= 1 && value <= totalPages)
      .sort((a, b) => a - b);
  }, [page, pagination.total_pages]);

  /* =======================================================
     EVENT CARD
     ======================================================= */

  const renderEvent = (rawEvent, index) => {
    const event = normaliseAuditEvent(rawEvent);

    const ActionIcon = getActionIcon(event.action);

    const AreaIcon = getAreaIcon(event.area);

    const {
      actorName,
      actorRole,
      actorEmail,
      organisationName,

      actingRole,
      actingOrganisationName,

      targetLabel,
      targetType,
      targetId,
    } = event;

    /* ===================================================
         CHANGES
         =================================================== */

    const changes = Object.entries(event.changes || {});

    /* ===================================================
         METADATA
         =================================================== */

    const metadataEntries = Object.entries(event.metadata || {});

    /*
     * Historical/event metadata.
     *
     * Current backend state is deliberately removed
     * from this group so it is not confused with the
     * original audit snapshot.
     */
    const metadata = metadataEntries.filter(
      ([key]) =>
        ![
          "acting_role",
          "acting_organisation_id",
          "acting_organisation_name",

          /*
           * Operation is already rendered explicitly
           * inside Event details.
           */
          "operation",

          ...CURRENT_CONTEXT_KEYS,
        ].includes(key),
    );

    /*
     * Current state retrieved from related backend
     * objects by PlatformAdminActivityView.
     */
    const currentContext = metadataEntries.filter(([key]) =>
      CURRENT_CONTEXT_KEYS.includes(key),
    );

    const hasDistinctActingContext =
      Boolean(actingRole && actorRole && actingRole !== actorRole) ||
      Boolean(
        actingOrganisationName && actingOrganisationName !== organisationName,
      );

    const isExpanded = expandedEventId === event.id;

    const hasDetails =
      changes.length > 0 ||
      metadata.length > 0 ||
      currentContext.length > 0 ||
      Boolean(targetType) ||
      targetId !== null ||
      Boolean(actorEmail) ||
      Boolean(organisationName) ||
      hasDistinctActingContext;

    return (
      <article
        key={event.id}
        className={`admin-activity-card ${isExpanded ? "is-expanded" : ""}`}
        style={{
          "--activity-index": index,
        }}
      >
        {/* =============================================
              RAIL
              ============================================= */}

        <div className="admin-activity-card__rail">
          <span className="admin-activity-card__rail-dot">
            <ActionIcon size={17} strokeWidth={2.15} />
          </span>
        </div>

        <div className="admin-activity-card__content">
          {/* ===========================================
                OPERATOR
                =========================================== */}

          <div className="admin-activity-card__top">
            <div className="admin-activity-card__identity">
              <div className="admin-activity-card__avatar">
                {getInitials(actorName)}
              </div>

              <div className="admin-activity-card__identity-copy">
                <div className="admin-activity-card__actor-line">
                  <strong>{actorName}</strong>

                  {actorRole ? (
                    <span className="admin-activity-card__role">
                      {t(`roles.${actorRole}`, {
                        defaultValue: actorRole,
                      })}
                    </span>
                  ) : null}
                </div>

                {organisationName ? (
                  <div className="admin-activity-card__organisation">
                    <Building2 size={14} strokeWidth={2} />

                    <span>{organisationName}</span>
                  </div>
                ) : null}

                {hasDistinctActingContext ? (
                  <div className="admin-activity-card__acting">
                    <span className="admin-activity-card__acting-label">
                      {t("common.actingOnBehalfOf", {
                        defaultValue: "On behalf of",
                      })}
                    </span>

                    {actingRole ? (
                      <span className="admin-activity-card__acting-role">
                        {t(`roles.${actingRole}`, {
                          defaultValue: actingRole,
                        })}
                      </span>
                    ) : null}

                    {actingOrganisationName ? (
                      <span className="admin-activity-card__acting-organisation">
                        <Building2 size={13} strokeWidth={2} />

                        {actingOrganisationName}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <time
              className="admin-activity-card__time"
              dateTime={event.created_at || undefined}
            >
              <Clock3 size={15} strokeWidth={2} />

              <span>{formatDateTime(event.created_at)}</span>
            </time>
          </div>

          {/* ===========================================
                EVENT SUMMARY
                =========================================== */}

          <div className="admin-activity-card__main">
            <div className="admin-activity-card__summary">
              <span
                className={`admin-activity-card__action admin-activity-card__action--${(
                  event.action || "default"
                ).toLowerCase()}`}
              >
                <ActionIcon size={15} strokeWidth={2.2} />

                {t(`actions.${event.action}`, {
                  defaultValue:
                    event.action || t("common.activity", "Activity"),
                })}
              </span>

              <div className="admin-activity-card__target">
                <span className="admin-activity-card__area-icon">
                  <AreaIcon size={17} strokeWidth={2} />
                </span>

                <div>
                  <span className="admin-activity-card__target-type">
                    {t(`areas.${event.area}`, {
                      defaultValue:
                        event.area || t("common.activity", "Activity"),
                    })}
                  </span>

                  <strong className="admin-activity-card__target-label">
                    {targetLabel || t("common.unknownTarget", "Unknown target")}
                  </strong>
                </div>
              </div>
            </div>

            {/* =========================================
                  DETAILS TOGGLE
                  ========================================= */}

            {hasDetails ? (
              <div className="admin-activity-card__details-toggle-row">
                <button
                  type="button"
                  className="admin-activity-card__details-toggle"
                  onClick={() =>
                    setExpandedEventId((current) =>
                      current === event.id ? null : event.id,
                    )
                  }
                  aria-expanded={isExpanded}
                  aria-controls={`activity-details-${event.id}`}
                >
                  <span>
                    {isExpanded
                      ? t("details.hide", "Hide details")
                      : t("details.show", "View details")}
                  </span>

                  <ChevronDown
                    size={16}
                    strokeWidth={2.1}
                    className={isExpanded ? "is-expanded" : ""}
                  />
                </button>
              </div>
            ) : null}

            {/* =========================================
                  EXPANDED DETAILS
                  ========================================= */}

            {isExpanded ? (
              <div
                id={`activity-details-${event.id}`}
                className="admin-activity-card__details"
              >
                {/* -------------------------------------
                      HISTORICAL CHANGES
                      ------------------------------------- */}

                {changes.length > 0 ? (
                  <section className="admin-activity-card__details-section">
                    <div className="admin-activity-card__details-heading">
                      <ArrowRight size={15} strokeWidth={2} />

                      <h4>{t("details.changes", "Changes")}</h4>
                    </div>

                    <div className="admin-activity-card__changes">
                      {changes.map(([fieldName, change]) => {
                        const isChangeObject =
                          change &&
                          typeof change === "object" &&
                          !Array.isArray(change) &&
                          (Object.prototype.hasOwnProperty.call(
                            change,
                            "from",
                          ) ||
                            Object.prototype.hasOwnProperty.call(change, "to"));

                        const before = isChangeObject ? change.from : null;

                        const after = isChangeObject ? change.to : change;

                        return (
                          <div
                            key={fieldName}
                            className="admin-activity-change"
                          >
                            <span className="admin-activity-change__field">
                              {t(`fields.${fieldName}`, {
                                defaultValue: humanizeKey(fieldName),
                              })}
                            </span>

                            <div className="admin-activity-change__comparison">
                              <div className="admin-activity-change__side">
                                <small>{t("details.before", "Before")}</small>

                                <div className="admin-activity-change__before">
                                  <AuditValue value={before} />
                                </div>
                              </div>

                              <span className="admin-activity-change__arrow">
                                <ArrowRight size={15} strokeWidth={2} />
                              </span>

                              <div className="admin-activity-change__side">
                                <small>{t("details.after", "After")}</small>

                                <div className="admin-activity-change__after">
                                  <AuditValue value={after} />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ) : null}

                {/* -------------------------------------
                      CURRENT BACKEND CONTEXT
                      ------------------------------------- */}

                {currentContext.length > 0 ? (
                  <section className="admin-activity-card__details-section admin-activity-card__details-section--current">
                    <div className="admin-activity-card__details-heading admin-activity-card__details-heading--current">
                      <Database size={16} strokeWidth={2} />

                      <div>
                        <h4>
                          {t(
                            "details.currentContext",
                            "Current backend context",
                          )}
                        </h4>

                        <p className="admin-activity-card__details-heading-note">
                          {t(
                            "details.currentContextNote",
                            "Current values related to this activity. These values may have changed since the original event was recorded.",
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="admin-activity-current-context">
                      {currentContext.map(([key, value]) => {
                        const isWide = [
                          "current_funding_sources",
                          "current_project_funding_source",
                        ].includes(key);

                        return (
                          <div
                            key={key}
                            className={`admin-activity-current-context__item ${
                              isWide
                                ? "admin-activity-current-context__item--wide"
                                : ""
                            }`}
                          >
                            <span className="admin-activity-current-context__label">
                              {t(`metadata.${key}`, {
                                defaultValue: formatContextKey(key),
                              })}
                            </span>

                            <div className="admin-activity-current-context__value">
                              <CurrentContextValue
                                contextKey={key}
                                value={value}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ) : null}

                {/* -------------------------------------
                      EVENT DETAILS
                      ------------------------------------- */}

                <section className="admin-activity-card__details-section">
                  <div className="admin-activity-card__details-heading">
                    <Info size={15} strokeWidth={2} />

                    <h4>{t("details.eventDetails", "Event details")}</h4>
                  </div>

                  <dl className="admin-activity-card__metadata">
                    {event.metadata?.operation ? (
                      <>
                        <dt>{t("details.operation", "Operation")}</dt>

                        <dd>{event.metadata.operation}</dd>
                      </>
                    ) : null}

                    <dt>{t("details.targetType", "Target type")}</dt>

                    <dd>{targetType || "—"}</dd>

                    <dt>{t("details.targetId", "Target ID")}</dt>

                    <dd>{targetId ?? "—"}</dd>

                    <dt>{t("details.actor", "Actor")}</dt>

                    <dd>{actorName}</dd>

                    {actorEmail ? (
                      <>
                        <dt>{t("details.actorEmail", "Actor email")}</dt>

                        <dd>{actorEmail}</dd>
                      </>
                    ) : null}

                    <dt>{t("details.actorRole", "Actor role")}</dt>

                    <dd>
                      {actorRole
                        ? t(`roles.${actorRole}`, {
                            defaultValue: actorRole,
                          })
                        : "—"}
                    </dd>

                    {organisationName ? (
                      <>
                        <dt>{t("details.organisation", "Organisation")}</dt>

                        <dd>{organisationName}</dd>
                      </>
                    ) : null}

                    {hasDistinctActingContext ? (
                      <>
                        <dt>{t("details.actingRole", "Acting role")}</dt>

                        <dd>
                          {actingRole
                            ? t(`roles.${actingRole}`, {
                                defaultValue: actingRole,
                              })
                            : "—"}
                        </dd>

                        {actingOrganisationName ? (
                          <>
                            <dt>
                              {t(
                                "details.actingOrganisation",
                                "Acting organisation",
                              )}
                            </dt>

                            <dd>{actingOrganisationName}</dd>
                          </>
                        ) : null}
                      </>
                    ) : null}

                    <dt>{t("details.time", "Time")}</dt>

                    <dd>{formatDateTime(event.created_at)}</dd>
                  </dl>
                </section>

                {/* -------------------------------------
                      HISTORICAL / EVENT METADATA
                      ------------------------------------- */}

                {metadata.length > 0 ? (
                  <section className="admin-activity-card__details-section">
                    <div className="admin-activity-card__details-heading">
                      <SlidersHorizontal size={15} strokeWidth={2} />

                      <div>
                        <h4>{t("details.metadata", "Additional context")}</h4>

                        <p className="admin-activity-card__details-heading-note">
                          {t(
                            "details.metadataNote",
                            "Context stored when this activity was recorded.",
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="admin-activity-card__metadata-grid">
                      {metadata.map(([key, value]) => (
                        <div
                          key={key}
                          className="admin-activity-card__metadata-item"
                        >
                          <span>
                            {t(`metadata.${key}`, {
                              defaultValue: humanizeKey(key),
                            })}
                          </span>

                          <div>
                            <AuditValue value={value} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </article>
    );
  };

  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <div className="admin-activity-page">
      <div className="admin-activity-page__inner">
        {/* ===============================================
            HEADER
            =============================================== */}

        <header className="admin-activity-header">
          <div>
            <div className="admin-activity-header__eyebrow">
              <Activity size={17} strokeWidth={2.2} />

              {t("eyebrow", "Platform administration")}
            </div>

            <h1 className="admin-activity-header__title">
              {t("title", "Platform Activity")}
            </h1>

            <p className="admin-activity-header__description">
              {t(
                "description",
                "Review important administrative and platform changes across users, organisations, dossiers, financing and renovation workflows.",
              )}
            </p>
          </div>

          <button
            type="button"
            className="admin-activity-refresh"
            onClick={() =>
              loadActivity({
                silent: true,
              })
            }
            disabled={refreshing}
          >
            <RefreshCw
              size={17}
              strokeWidth={2.1}
              className={refreshing ? "is-spinning" : ""}
            />

            <span>
              {refreshing
                ? t("refreshing", "Refreshing...")
                : t("refresh", "Refresh")}
            </span>
          </button>
        </header>

        {/* ===============================================
            AREA PILLS
            =============================================== */}

        <section className="admin-activity-area-section">
          <div className="admin-activity-area-pills">
            {AREA_FILTERS.map((item) => {
              const Icon = item.icon;

              const active = area === item.value;

              return (
                <button
                  key={item.value}
                  type="button"
                  className={`admin-activity-area-pill ${
                    active ? "is-active" : ""
                  }`}
                  onClick={() => handleAreaChange(item.value)}
                  aria-pressed={active}
                >
                  <Icon size={16} strokeWidth={2.1} />

                  <span>
                    {t(`areas.${item.value}`, {
                      defaultValue:
                        item.value === "ALL" ? "All" : humanizeKey(item.value),
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* ===============================================
            FILTER PANEL
            =============================================== */}

        <section className="admin-activity-filters">
          <div className="admin-activity-filters__mobile-bar">
            <button
              type="button"
              className="admin-activity-filter-toggle"
              onClick={() => setMobileFiltersOpen((current) => !current)}
            >
              <SlidersHorizontal size={18} strokeWidth={2} />

              <span>{t("filters.title", "Filters")}</span>

              {hasActiveFilters ? (
                <span className="admin-activity-filter-toggle__dot" />
              ) : null}
            </button>
          </div>

          <div
            className={`admin-activity-filters__content ${
              mobileFiltersOpen ? "is-open" : ""
            }`}
          >
            <div className="admin-activity-filter-grid">
              {/* SEARCH */}

              <label className="admin-activity-field admin-activity-field--search">
                <span className="admin-activity-field__label">
                  {t("filters.searchLabel", "Search")}
                </span>

                <div className="admin-activity-search">
                  <Search size={18} strokeWidth={2} />

                  <input
                    type="search"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder={t(
                      "filters.searchPlaceholder",
                      "Search actor, target or organisation",
                    )}
                  />

                  {searchInput ? (
                    <button
                      type="button"
                      className="admin-activity-search__clear"
                      onClick={() => setSearchInput("")}
                      aria-label={t("filters.clearSearch", "Clear search")}
                    >
                      <X size={16} />
                    </button>
                  ) : null}
                </div>
              </label>

              {/* ACTION */}

              <label className="admin-activity-field">
                <span className="admin-activity-field__label">
                  {t("filters.action", "Action")}
                </span>

                <select value={action} onChange={handleActionChange}>
                  {ACTION_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value === "ALL"
                        ? t("filters.allActions", "All actions")
                        : t(`actions.${value}`, {
                            defaultValue: humanizeKey(value),
                          })}
                    </option>
                  ))}
                </select>
              </label>

              {/* ACTOR */}

              <label className="admin-activity-field">
                <span className="admin-activity-field__label">
                  {t("filters.actor", "Actor")}
                </span>

                <select value={actorRole} onChange={handleRoleChange}>
                  {ROLE_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value === "ALL"
                        ? t("filters.allActors", "All actors")
                        : t(`roles.${value}`, {
                            defaultValue: humanizeKey(value),
                          })}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* DATE PRESETS */}

            <div className="admin-activity-date-row">
              <div className="admin-activity-date-presets">
                <div className="admin-activity-date-presets__label">
                  <CalendarDays size={16} strokeWidth={2} />

                  <span>{t("filters.period", "Period")}</span>
                </div>

                <div className="admin-activity-date-presets__pills">
                  {DATE_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={`admin-activity-date-pill ${
                        datePreset === preset ? "is-active" : ""
                      }`}
                      onClick={() => handlePresetChange(preset)}
                    >
                      {t(`datePresets.${preset}`, {
                        defaultValue:
                          preset === "ALL"
                            ? "All time"
                            : preset === "CUSTOM"
                              ? "Custom"
                              : humanizeKey(preset),
                      })}
                    </button>
                  ))}
                </div>
              </div>

              {datePreset === "CUSTOM" ? (
                <div className="admin-activity-custom-dates">
                  <label>
                    <span>{t("filters.from", "From")}</span>

                    <input
                      type="date"
                      value={dateFrom}
                      onChange={handleDateFromChange}
                    />
                  </label>

                  <span className="admin-activity-custom-dates__separator">
                    —
                  </span>

                  <label>
                    <span>{t("filters.to", "To")}</span>

                    <input
                      type="date"
                      value={dateTo}
                      onChange={handleDateToChange}
                    />
                  </label>
                </div>
              ) : null}

              {hasActiveFilters ? (
                <button
                  type="button"
                  className="admin-activity-clear-filters"
                  onClick={clearFilters}
                >
                  <X size={15} strokeWidth={2.2} />

                  {t("filters.clearAll", "Clear all")}
                </button>
              ) : null}
            </div>
          </div>
        </section>

        {/* ===============================================
            RESULT SUMMARY
            =============================================== */}

        <div className="admin-activity-results-header">
          <div>
            <h2>{t("results.title", "Activity log")}</h2>

            {!loading ? (
              <p>
                {t("results.count", "{{count}} activities", {
                  count: pagination.total_items,
                })}
              </p>
            ) : null}
          </div>
        </div>

        {/* ===============================================
            LOADING
            =============================================== */}

        {loading ? (
          <div className="admin-activity-list">
            {Array.from({
              length: 6,
            }).map((_, index) => (
              <div key={index} className="admin-activity-skeleton">
                <span className="admin-activity-skeleton__avatar" />

                <div className="admin-activity-skeleton__body">
                  <span className="admin-activity-skeleton__line admin-activity-skeleton__line--large" />

                  <span className="admin-activity-skeleton__line admin-activity-skeleton__line--medium" />

                  <span className="admin-activity-skeleton__line admin-activity-skeleton__line--small" />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* ===============================================
            ERROR
            =============================================== */}

        {!loading && error ? (
          <div className="admin-activity-state admin-activity-state--error">
            <div className="admin-activity-state__icon">
              <XCircle size={28} strokeWidth={1.9} />
            </div>

            <h3>{t("errors.title", "Activity unavailable")}</h3>

            <p>{error}</p>

            <button type="button" onClick={() => loadActivity()}>
              <RefreshCw size={16} strokeWidth={2} />

              {t("errors.retry", "Try again")}
            </button>
          </div>
        ) : null}

        {/* ===============================================
            EMPTY
            =============================================== */}

        {!loading && !error && events.length === 0 ? (
          <div className="admin-activity-state">
            <div className="admin-activity-state__icon">
              <Activity size={29} strokeWidth={1.8} />
            </div>

            <h3>{t("empty.title", "No activity found")}</h3>

            <p>
              {hasActiveFilters
                ? t(
                    "empty.filteredDescription",
                    "No platform activity matches the selected filters.",
                  )
                : t(
                    "empty.description",
                    "Platform activity will appear here as changes are recorded.",
                  )}
            </p>

            {hasActiveFilters ? (
              <button type="button" onClick={clearFilters}>
                <X size={16} strokeWidth={2} />

                {t("filters.clearAll", "Clear all")}
              </button>
            ) : null}
          </div>
        ) : null}

        {/* ===============================================
            EVENTS
            =============================================== */}

        {!loading && !error && events.length > 0 ? (
          <>
            <div className="admin-activity-list">{events.map(renderEvent)}</div>

            {/* =========================================
                PAGINATION
                ========================================= */}

            {pagination.total_pages > 1 ? (
              <nav
                className="admin-activity-pagination"
                aria-label={t("pagination.aria", "Activity pagination")}
              >
                <button
                  type="button"
                  className="admin-activity-pagination__nav"
                  disabled={!pagination.has_previous}
                  onClick={() => {
                    setExpandedEventId(null);

                    setPage((current) => Math.max(1, current - 1));
                  }}
                >
                  <ChevronLeft size={17} strokeWidth={2.2} />

                  <span>{t("pagination.previous", "Previous")}</span>
                </button>

                <div className="admin-activity-pagination__pages">
                  {visiblePages.map((pageNumber, index) => {
                    const previous = visiblePages[index - 1];

                    const showGap = previous && pageNumber - previous > 1;

                    return (
                      <React.Fragment key={pageNumber}>
                        {showGap ? (
                          <span className="admin-activity-pagination__ellipsis">
                            …
                          </span>
                        ) : null}

                        <button
                          type="button"
                          className={`admin-activity-pagination__page ${
                            page === pageNumber ? "is-active" : ""
                          }`}
                          onClick={() => {
                            setExpandedEventId(null);

                            setPage(pageNumber);
                          }}
                          aria-current={
                            page === pageNumber ? "page" : undefined
                          }
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="admin-activity-pagination__nav"
                  disabled={!pagination.has_next}
                  onClick={() => {
                    setExpandedEventId(null);

                    setPage((current) => current + 1);
                  }}
                >
                  <span>{t("pagination.next", "Next")}</span>

                  <ChevronRight size={17} strokeWidth={2.2} />
                </button>
              </nav>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
